import {Directive, ElementRef, HostListener, inject} from '@angular/core';
import {NgControl} from '@angular/forms';

/**
 * Turns a plain text input into a dd.MM.yyyy field: the separators are inserted while typing,
 * only digits are accepted, pasted dates are normalized.
 */
@Directive({
  selector: 'input[appDateMask]',
  host: {
    'inputmode': 'numeric',
    'maxlength': '10'
  }
})
export class DateMaskDirective {
  private readonly input: HTMLInputElement = inject<ElementRef<HTMLInputElement>>(ElementRef).nativeElement;
  private readonly ngControl = inject(NgControl, {optional: true});

  @HostListener('input')
  onInput(): void {
    const caret = this.input.selectionStart ?? this.input.value.length;
    const digitsBeforeCaret = DateMaskDirective.digitsOf(this.input.value.slice(0, caret)).length;

    this.write(DateMaskDirective.digitsOf(this.input.value), digitsBeforeCaret);
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const start = this.input.selectionStart ?? 0;
    const end = this.input.selectionEnd ?? 0;
    if (start !== end) {
      return;
    }

    // Backspace on a separator removes the digit in front of it instead of getting stuck.
    if (event.key === 'Backspace' && this.input.value[start - 1] === '.') {
      event.preventDefault();
      const value = this.input.value.slice(0, start - 2) + this.input.value.slice(start);
      const digitsBeforeCaret = DateMaskDirective.digitsOf(value.slice(0, start - 2)).length;
      this.write(DateMaskDirective.digitsOf(value), digitsBeforeCaret);
    }

    // Delete in front of a separator removes the digit behind it.
    if (event.key === 'Delete' && this.input.value[start] === '.') {
      event.preventDefault();
      const value = this.input.value.slice(0, start) + this.input.value.slice(start + 2);
      const digitsBeforeCaret = DateMaskDirective.digitsOf(value.slice(0, start)).length;
      this.write(DateMaskDirective.digitsOf(value), digitsBeforeCaret);
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const pasted = event.clipboardData?.getData('text') ?? '';
    const digits = DateMaskDirective.digitsOfParts(pasted.split(/\D+/).filter(part => part.length));
    this.write(digits, digits.length);
  }

  private write(digits: string, digitsBeforeCaret: number): void {
    const value = DateMaskDirective.format(digits);

    this.input.value = value;
    const caret = DateMaskDirective.caretAfterDigits(value, digitsBeforeCaret);
    this.input.setSelectionRange(caret, caret);

    // The view is already up to date, only the model has to follow.
    this.ngControl?.control?.setValue(value, {emitModelToViewChange: false});
  }

  private static digitsOf(value: string): string {
    return value.replace(/\D/g, '');
  }

  /** Pads a "1.2.90" style input to "01.02.1990". */
  private static digitsOfParts(parts: string[]): string {
    if (parts.length !== 3) {
      return DateMaskDirective.digitsOf(parts.join(''));
    }

    const [day, month, year] = parts;
    return day.padStart(2, '0') + month.padStart(2, '0') + DateMaskDirective.expandYear(year);
  }

  private static expandYear(year: string): string {
    if (year.length !== 2) {
      return year;
    }

    const century = Number(year) <= new Date().getFullYear() % 100 ? 2000 : 1900;
    return String(century + Number(year));
  }

  private static format(digits: string): string {
    const date = digits.slice(0, 8);
    return [date.slice(0, 2), date.slice(2, 4), date.slice(4, 8)].filter(part => part.length).join('.');
  }

  private static caretAfterDigits(value: string, digits: number): number {
    if (digits === 0) {
      return 0;
    }

    let seen = 0;
    for (let index = 0; index < value.length; index++) {
      if (/\d/.test(value[index]) && ++seen === digits) {
        return index + 1;
      }
    }

    return value.length;
  }
}
